import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePremiumFeature } from '../components/PremiumFeature';
import { 
    ArrowLeft, 
    User, 
    LogOut, 
    Bell, 
    Moon, 
    Sun,
    Eye,
    Sparkles,
    Info,
    Shield,
    Mail,
    Heart,
    ChevronRight,
    Check,
    Settings as SettingsIcon,
    CreditCard,
    FileText,
    XCircle,
    SkipForward,
    HelpCircle,
    Volume2
} from 'lucide-react';
import { toast } from 'sonner';
import { CoRegulateLogo, CoRegulateMark } from '../components/Logo';
import { PrimaryButton, SecondaryButton, TertiaryButton, IconButton } from '../components/brand/BrandButton';
import { BrandCard } from '../components/brand/BrandCard';
import { Heading, BodyText, SmallText, Tagline } from '../components/brand/BrandTypography';
import { IconContainer } from '../components/brand/BrandIcon';
import { Container } from '../components/brand/BrandLayout';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Settings = () => {
    const navigate = useNavigate();
    const { user, logout, canAccess, refreshUser } = useAuth();
    const { checkAccess } = usePremiumFeature();
    
    // Local state for settings
    const [calmingReminders, setCalmingReminders] = useState(false);
    const [theme, setTheme] = useState('light');
    const [largeText, setLargeText] = useState(false);
    const [reducedMotion, setReducedMotion] = useState(false);
    const [skipSettling, setSkipSettling] = useState(() => {
        return localStorage.getItem('coregulate_skip_settling') === 'true';
    });
    
    // Subscription state
    const [billingHistory, setBillingHistory] = useState([]);
    const [billingLoading, setBillingLoading] = useState(false);
    const [showBillingHistory, setShowBillingHistory] = useState(false);
    const [cancelLoading, setCancelLoading] = useState(false);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const fetchBillingHistory = async () => {
        setBillingLoading(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/subscription/billing-history`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setBillingHistory(data.invoices || []);
            }
        } catch (error) {
            toast.error('Failed to load billing history');
        } finally {
            setBillingLoading(false);
        }
    };

    const handleCancelSubscription = async () => {
        setCancelLoading(true);
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/subscription/cancel`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                toast.success('Subscription will be canceled at end of billing period');
                setShowCancelConfirm(false);
                if (refreshUser) refreshUser();
            } else {
                const data = await response.json();
                toast.error(data.detail || 'Failed to cancel subscription');
            }
        } catch (error) {
            toast.error('Failed to cancel subscription');
        } finally {
            setCancelLoading(false);
        }
    };

    const handleReactivateSubscription = async () => {
        try {
            const token = localStorage.getItem('co_regulate_token');
            const response = await fetch(`${API_URL}/api/subscription/reactivate`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                toast.success('Subscription reactivated!');
                if (refreshUser) refreshUser();
            } else {
                toast.error('Failed to reactivate subscription');
            }
        } catch (error) {
            toast.error('Failed to reactivate subscription');
        }
    };

    const handleLogout = () => {
        logout();
        toast.success('Take care of yourself');
        navigate('/');
    };

    // Handle calming reminders toggle - premium feature trigger
    const handleCalmingRemindersToggle = () => {
        if (!canAccess('reminders')) {
            // Trigger paywall for non-subscribers
            checkAccess('reminders', () => {
                setCalmingReminders(!calmingReminders);
            });
        } else {
            setCalmingReminders(!calmingReminders);
            toast.success(calmingReminders ? 'Reminders turned off' : 'Reminders turned on', {
                description: calmingReminders ? 'You won\'t receive check-in nudges.' : 'We\'ll gently remind you to check in.'
            });
        }
    };

    // Toggle component - gentle and spacious
    const Toggle = ({ enabled, onChange, disabled = false, premium = false }) => (
        <button
            onClick={onChange}
            disabled={disabled}
            className={`
                relative w-14 h-8 rounded-full transition-all duration-500 ease-out
                ${enabled 
                    ? 'bg-[#7D8E95]' 
                    : 'bg-[#FFBB98]'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                focus:outline-none focus:ring-2 focus:ring-[#7D8E95]/30 focus:ring-offset-2 focus:ring-offset-[#FBE0C3]
            `}
            data-testid="toggle-btn"
        >
            <span
                className={`
                    absolute top-1 w-6 h-6 rounded-full bg-white shadow-sm
                    transition-all duration-500 ease-out
                    ${enabled ? 'left-7' : 'left-1'}
                `}
            />
            {premium && !enabled && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-[#7D8E95] flex items-center justify-center">
                    <Sparkles className="w-2.5 h-2.5 text-white" />
                </span>
            )}
        </button>
    );

    // Section header component
    const SectionHeader = ({ children }) => (
        <div className="mb-4">
            <SmallText className="uppercase tracking-wider text-[#7D8E95] font-medium">
                {children}
            </SmallText>
        </div>
    );

    // Settings row component
    const SettingsRow = ({ 
        icon: Icon, 
        iconVariant = 'sage',
        title, 
        description, 
        onClick, 
        rightElement,
        premium = false,
        testId
    }) => (
        <div
            onClick={rightElement ? undefined : onClick}
            className={`w-full flex items-center gap-4 p-4 rounded-2xl glass-card card-hover text-left group ${!rightElement ? 'cursor-pointer' : ''}`}
            role={!rightElement ? 'button' : undefined}
            tabIndex={!rightElement ? 0 : undefined}
            onKeyDown={!rightElement ? (e) => e.key === 'Enter' && onClick?.() : undefined}
            data-testid={testId}
        >
            <IconContainer variant={iconVariant} size="md" useGradient={true}>
                <Icon className="w-5 h-5" />
            </IconContainer>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <BodyText className="font-medium text-[#1D6B6B]">{title}</BodyText>
                    {premium && !canAccess('reminders') && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-[#7D8E95]/20 text-[#7D8E95] font-medium flex items-center gap-1">
                            <Sparkles className="w-3 h-3" />
                            Support
                        </span>
                    )}
                </div>
                {description && (
                    <SmallText className="text-[#7D8E95]">{description}</SmallText>
                )}
            </div>
            {rightElement || (
                <ChevronRight className="w-5 h-5 text-[#7D8E95] group-hover:text-[#7D8E95] transition-colors" />
            )}
        </div>
    );

    return (
        <div className="min-h-screen bg-transparent">
            {/* Header - glass-morphism */}
            <header className="sticky top-0 z-50 glass-header">
                <Container size="sm" className="py-5 flex items-center justify-between">
                    <IconButton 
                        onClick={() => navigate('/home')}
                        data-testid="back-btn"
                        className="text-[#8B7B6B] hover:text-[#6B5D4D] hover:bg-[#FFBB98]/40 transition-all duration-200"
                    >
                        <ArrowLeft className="w-5 h-5" />
                    </IconButton>
                    <Heading as="h1" size="text-lg">Settings</Heading>
                    <button
                        onClick={() => navigate('/home')}
                        className="hover:opacity-80 transition-opacity"
                        data-testid="header-logo"
                        aria-label="Go to dashboard"
                    >
                        <CoRegulateMark size={28} />
                    </button>
                </Container>
            </header>

            <main className="max-w-xl mx-auto px-6 py-8">
                
                {/* ══════════════════════════════════════════════════════════
                    SECTION 1: ACCOUNT
                ══════════════════════════════════════════════════════════ */}
                <section className="mb-10 brand-fade-in">
                    <SectionHeader>Account</SectionHeader>
                    <div className="glass-card rounded-3xl p-6">
                        <div className="flex items-center gap-5">
                            <div 
                                className="w-16 h-16 rounded-full flex items-center justify-center icon-glow-sage"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(52,70,72,0.25) 0%, rgba(52,70,72,0.15) 100%)',
                                    border: '2px solid rgba(52,70,72,0.3)'
                                }}
                            >
                                <User className="w-7 h-7 text-[#1D6B6B]" />
                            </div>
                            <div className="flex-1">
                                <Heading as="h2" size="text-lg" className="mb-0.5">
                                    {user?.name || 'Guest'}
                                </Heading>
                                <SmallText className="text-[#7D8E95]">
                                    {user?.email || 'Not signed in'}
                                </SmallText>
                            </div>
                        </div>
                        
                        {/* Sign out button - gentle and non-urgent */}
                        <div className="mt-6 pt-5 border-t border-white/40/50">
                            <button
                                onClick={handleLogout}
                                className="flex items-center gap-3 text-[#FFBB98] hover:text-[#FFBB98] transition-colors"
                                data-testid="logout-btn"
                            >
                                <LogOut className="w-4 h-4" />
                                <SmallText className="font-medium">Sign out</SmallText>
                            </button>
                        </div>
                    </div>
                </section>

                {/* ══════════════════════════════════════════════════════════
                    SECTION 2: NOTIFICATIONS
                ══════════════════════════════════════════════════════════ */}
                <section className="mb-10 brand-fade-in" style={{ animationDelay: '50ms' }}>
                    <SectionHeader>Notifications</SectionHeader>
                    <div className="space-y-3">
                        <SettingsRow
                            icon={Bell}
                            iconVariant="gold"
                            title="Calming Reminders"
                            description="Gentle nudges to check in with yourself"
                            onClick={handleCalmingRemindersToggle}
                            premium={true}
                            rightElement={
                                <Toggle 
                                    enabled={calmingReminders} 
                                    onChange={handleCalmingRemindersToggle}
                                    premium={!canAccess('reminders')}
                                />
                            }
                            testId="calming-reminders-toggle"
                        />
                    </div>
                </section>

                {/* ══════════════════════════════════════════════════════════
                    SECTION 3: APP SETTINGS
                ══════════════════════════════════════════════════════════ */}
                <section className="mb-10 brand-fade-in" style={{ animationDelay: '100ms' }}>
                    <SectionHeader>App Settings</SectionHeader>
                    <div className="space-y-3">
                        {/* Theme Selection */}
                        <div className="p-4 rounded-2xl bg-white/60">
                            <div className="flex items-center gap-4 mb-4">
                                <IconContainer variant="lavender" size="md" useGradient={true}>
                                    {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
                                </IconContainer>
                                <div>
                                    <BodyText className="font-medium text-[#1D6B6B]">Appearance</BodyText>
                                    <SmallText className="text-[#7D8E95]">Choose your visual comfort</SmallText>
                                </div>
                            </div>
                            <div className="flex gap-2 ml-14 p-1.5 bg-[#FFBB98]/40 rounded-2xl w-fit">
                                {['light', 'dark', 'auto'].map((option) => (
                                    <button
                                        key={option}
                                        onClick={() => {
                                            setTheme(option);
                                            toast.success(`Theme set to ${option}`);
                                        }}
                                        className={`
                                            px-4 py-2.5 rounded-xl text-sm font-medium transition-all duration-300
                                            ${theme === option 
                                                ? 'bg-white/90 backdrop-blur-sm text-[#1D6B6B] shadow-soft' 
                                                : 'text-[#7D8E95] hover:text-[#2A8585] hover:bg-white/40'
                                            }
                                        `}
                                        data-testid={`theme-${option}`}
                                    >
                                        {option === 'light' && 'Light'}
                                        {option === 'dark' && 'Dark'}
                                        {option === 'auto' && 'Auto'}
                                        {theme === option && <Check className="w-3 h-3 inline ml-1.5" />}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Accessibility Options */}
                        <div className="p-4 rounded-2xl bg-white/60">
                            <div className="flex items-center gap-4 mb-4">
                                <IconContainer variant="blue" size="md" useGradient={true}>
                                    <Eye className="w-5 h-5" />
                                </IconContainer>
                                <div>
                                    <BodyText className="font-medium text-[#1D6B6B]">Accessibility</BodyText>
                                    <SmallText className="text-[#7D8E95]">Adjust for your comfort</SmallText>
                                </div>
                            </div>
                            <div className="space-y-4 ml-14">
                                {/* Large Text Toggle */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <BodyText className="text-sm font-medium text-[#2A8585]">Large Text</BodyText>
                                        <SmallText className="text-xs text-[#7D8E95]">Increase text size for readability</SmallText>
                                    </div>
                                    <Toggle 
                                        enabled={largeText} 
                                        onChange={() => {
                                            setLargeText(!largeText);
                                            toast.success(largeText ? 'Standard text size' : 'Large text enabled');
                                        }}
                                    />
                                </div>
                                
                                {/* Reduced Motion Toggle */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <BodyText className="text-sm font-medium text-[#2A8585]">Reduced Motion</BodyText>
                                        <SmallText className="text-xs text-[#7D8E95]">Minimize animations</SmallText>
                                    </div>
                                    <Toggle 
                                        enabled={reducedMotion} 
                                        onChange={() => {
                                            setReducedMotion(!reducedMotion);
                                            toast.success(reducedMotion ? 'Animations enabled' : 'Reduced motion enabled');
                                        }}
                                    />
                                </div>
                                
                                {/* Skip Settling Page Toggle */}
                                <div className="flex items-center justify-between">
                                    <div>
                                        <BodyText className="text-sm font-medium text-[#2A8585]">Skip Settling Page</BodyText>
                                        <SmallText className="text-xs text-[#7D8E95]">Go directly to dashboard on login</SmallText>
                                    </div>
                                    <Toggle 
                                        enabled={skipSettling} 
                                        onChange={() => {
                                            const newValue = !skipSettling;
                                            setSkipSettling(newValue);
                                            localStorage.setItem('coregulate_skip_settling', newValue.toString());
                                            toast.success(newValue ? 'Settling page will be skipped' : 'Settling page enabled');
                                        }}
                                    />
                                </div>
                                
                                {/* Restart Tour Button */}
                                <div className="flex items-center justify-between pt-2">
                                    <div>
                                        <BodyText className="text-sm font-medium text-[#2A8585]">Dashboard Tour</BodyText>
                                        <SmallText className="text-xs text-[#7D8E95]">Learn how to use the app</SmallText>
                                    </div>
                                    <button
                                        onClick={() => {
                                            localStorage.removeItem('coregulate_tour_completed');
                                            toast.success('Tour reset! Visit the dashboard to see it again.');
                                        }}
                                        className="flex items-center gap-2 px-3 py-1.5 text-sm text-[#1D6B6B] hover:bg-[#7D8E95]/10 rounded-lg transition-colors"
                                    >
                                        <HelpCircle className="w-4 h-4" />
                                        Restart
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Sound Preferences */}
                        <SettingsRow
                            icon={Volume2}
                            iconVariant="gold"
                            title="Sound Preferences"
                            description="Reset breathing sound preferences"
                            onClick={() => {
                                localStorage.removeItem('breathSoundPreferenceAsked');
                                toast.success('Sound preferences reset', {
                                    description: 'You\'ll be asked again next time you use a breathing tool.'
                                });
                            }}
                            testId="sound-preferences-btn"
                        />
                    </div>
                </section>

                {/* ══════════════════════════════════════════════════════════
                    SECTION 4: UPGRADE
                ══════════════════════════════════════════════════════════ */}
                <section className="mb-10 brand-fade-in" style={{ animationDelay: '150ms' }}>
                    <SectionHeader>Your Support Level</SectionHeader>
                    <div 
                        className="rounded-3xl p-6"
                        style={{
                            background: 'linear-gradient(135deg, rgba(52,70,72,0.12) 0%, rgba(125,142,149,0.08) 100%)',
                            border: '1px solid rgba(52,70,72,0.25)'
                        }}
                    >
                        <div className="flex items-start gap-4 mb-6">
                            <div 
                                className="w-12 h-12 rounded-2xl flex items-center justify-center"
                                style={{
                                    background: 'linear-gradient(135deg, rgba(52,70,72,0.3) 0%, rgba(52,70,72,0.15) 100%)'
                                }}
                            >
                                <Heart className="w-6 h-6 text-[#1D6B6B]" />
                            </div>
                            <div className="flex-1">
                                <Heading as="h3" size="text-lg" className="mb-1 text-[#1D6B6B]">
                                    Upgrade for more support
                                </Heading>
                                <BodyText className="text-sm text-[#2A8585] leading-relaxed">
                                    Access extended tools, reminders, and relational support features — at your own pace.
                                </BodyText>
                            </div>
                        </div>
                        
                        {/* Upgrade Button - spacious and gentle */}
                        <button
                            onClick={() => navigate('/pricing')}
                            className="w-full py-4 px-6 rounded-2xl bg-[#7D8E95] hover:bg-[#9AB892] text-white font-medium transition-all duration-300 flex items-center justify-center gap-2 shadow-sm hover:shadow-md"
                            data-testid="upgrade-btn"
                        >
                            <Sparkles className="w-5 h-5" />
                            <span>Explore Plans</span>
                        </button>
                        
                        {/* Reassuring note */}
                        <p className="mt-4 text-center">
                            <SmallText className="text-[#7D8E95]">
                                No pressure. Your nervous system comes first.
                            </SmallText>
                        </p>
                    </div>
                </section>

                {/* ══════════════════════════════════════════════════════════
                    SECTION 4B: SUBSCRIPTION MANAGEMENT (for paying users)
                ══════════════════════════════════════════════════════════ */}
                {user?.subscription_tier && user.subscription_tier !== 'free' && (
                    <section className="mb-10 brand-fade-in" style={{ animationDelay: '175ms' }}>
                        <SectionHeader>Subscription</SectionHeader>
                        <div className="space-y-3">
                            {/* Current Plan */}
                            <div className="p-4 rounded-2xl bg-white/60">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <IconContainer variant="sage" size="md" useGradient={true}>
                                            <CreditCard className="w-5 h-5" />
                                        </IconContainer>
                                        <div>
                                            <BodyText className="font-medium text-[#1D6B6B]">
                                                {user.subscription_tier === 'partner' ? 'Deep Support' : 'Support+'} Plan
                                            </BodyText>
                                            <SmallText className="text-[#7D8E95]">
                                                {user.subscription_billing === 'yearly' ? 'Billed yearly' : 'Billed monthly'}
                                            </SmallText>
                                        </div>
                                    </div>
                                    {user.subscription_cancel_at_period_end && (
                                        <span className="px-2 py-1 text-xs bg-[#F4D7D9] text-[#FFBB98] rounded-full">
                                            Canceling
                                        </span>
                                    )}
                                </div>
                            </div>
                            
                            {/* Billing History */}
                            <SettingsRow
                                icon={FileText}
                                iconVariant="blue"
                                title="Billing History"
                                description="View past invoices and receipts"
                                onClick={() => {
                                    setShowBillingHistory(true);
                                    fetchBillingHistory();
                                }}
                                testId="billing-history-btn"
                            />
                            
                            {/* Cancel / Reactivate */}
                            {user.subscription_cancel_at_period_end ? (
                                <SettingsRow
                                    icon={Heart}
                                    iconVariant="sage"
                                    title="Reactivate Subscription"
                                    description="Keep your access to premium features"
                                    onClick={handleReactivateSubscription}
                                    testId="reactivate-btn"
                                />
                            ) : (
                                <SettingsRow
                                    icon={XCircle}
                                    iconVariant="rose"
                                    title="Cancel Subscription"
                                    description="You'll keep access until period ends"
                                    onClick={() => setShowCancelConfirm(true)}
                                    testId="cancel-subscription-btn"
                                />
                            )}
                        </div>
                    </section>
                )}

                {/* ══════════════════════════════════════════════════════════
                    SECTION 5: ADMIN (for authorized users)
                ══════════════════════════════════════════════════════════ */}
                <section className="mb-10 brand-fade-in" style={{ animationDelay: '200ms' }}>
                    <SectionHeader>Admin</SectionHeader>
                    <div className="space-y-3">
                        <SettingsRow
                            icon={SettingsIcon}
                            iconVariant="blue"
                            title="Tool Editor"
                            description="Manage pages, tools, and content"
                            onClick={() => navigate('/admin/tools')}
                            testId="admin-tools-link"
                        />
                    </div>
                </section>

                {/* ══════════════════════════════════════════════════════════
                    SECTION 6: ABOUT
                ══════════════════════════════════════════════════════════ */}
                <section className="mb-10 brand-fade-in" style={{ animationDelay: '250ms' }}>
                    <SectionHeader>About</SectionHeader>
                    <div className="space-y-3">
                        <SettingsRow
                            icon={Info}
                            iconVariant="sage"
                            title="App Version"
                            description="CoRegulate v1.0.0"
                            onClick={() => toast.info('You\'re on the latest version')}
                            rightElement={
                                <SmallText className="text-[#7D8E95] font-medium">v1.0.0</SmallText>
                            }
                            testId="app-version"
                        />
                        <SettingsRow
                            icon={Shield}
                            iconVariant="blue"
                            title="Privacy & Terms"
                            description="How we protect your data"
                            onClick={() => toast.info('Privacy policy coming soon')}
                            testId="privacy-terms"
                        />
                        <SettingsRow
                            icon={Mail}
                            iconVariant="lavender"
                            title="Support"
                            description="Get help or submit a support ticket"
                            onClick={() => navigate('/support')}
                            testId="contact-support"
                        />
                    </div>
                </section>

                {/* Footer - gentle branding */}
                <footer className="pt-8 pb-12 text-center brand-fade-in" style={{ animationDelay: '250ms' }}>
                    <CoRegulateLogo size="sm" className="justify-center mb-3 opacity-60" />
                    <Tagline size="sm" className="opacity-60" />
                    <SmallText className="mt-4 text-[#7D8E95]">
                        Made with care for ADHD hearts
                    </SmallText>
                </footer>
            </main>

            {/* Billing History Modal */}
            {showBillingHistory && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-lg w-full max-h-[80vh] overflow-y-auto p-6">
                        <div className="flex items-center justify-between mb-4">
                            <Heading as="h3" size="text-lg">Billing History</Heading>
                            <IconButton onClick={() => setShowBillingHistory(false)}>
                                <ArrowLeft className="w-5 h-5" />
                            </IconButton>
                        </div>
                        
                        {billingLoading ? (
                            <div className="text-center py-8">
                                <div className="w-3 h-3 rounded-full bg-[#1D6B6B] animate-pulse mx-auto mb-4" />
                                <SmallText className="text-[#7D8E95]">Loading invoices...</SmallText>
                            </div>
                        ) : billingHistory.length === 0 ? (
                            <div className="text-center py-8">
                                <SmallText className="text-[#7D8E95]">No billing history found</SmallText>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {billingHistory.map(invoice => (
                                    <div key={invoice.id} className="p-4 rounded-xl bg-[#F9F7F4] border border-white/40">
                                        <div className="flex items-center justify-between mb-2">
                                            <BodyText className="font-medium">
                                                ${invoice.amount.toFixed(2)} {invoice.currency}
                                            </BodyText>
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                invoice.status === 'paid' ? 'bg-[#7D8E95]/20 text-[#1D6B6B]' : 'bg-[#FFBB98] text-[#7D8E95]'
                                            }`}>
                                                {invoice.status}
                                            </span>
                                        </div>
                                        <SmallText className="text-[#7D8E95] block">
                                            {new Date(invoice.date * 1000).toLocaleDateString()}
                                        </SmallText>
                                        {invoice.invoice_pdf && (
                                            <a 
                                                href={invoice.invoice_pdf} 
                                                target="_blank" 
                                                rel="noopener noreferrer"
                                                className="text-sm text-[#7D8E95] hover:underline mt-2 inline-block"
                                            >
                                                Download PDF
                                            </a>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Cancel Confirmation Modal */}
            {showCancelConfirm && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <div className="text-center mb-6">
                            <div className="w-16 h-16 rounded-full bg-[#F4D7D9] flex items-center justify-center mx-auto mb-4">
                                <XCircle className="w-8 h-8 text-[#FFBB98]" />
                            </div>
                            <Heading as="h3" size="text-lg" className="mb-2">Cancel Subscription?</Heading>
                            <BodyText className="text-[#7D8E95]">
                                You'll keep access to premium features until the end of your current billing period. You can reactivate anytime.
                            </BodyText>
                        </div>
                        
                        <div className="flex gap-3">
                            <TertiaryButton 
                                onClick={() => setShowCancelConfirm(false)} 
                                className="flex-1"
                            >
                                Keep Subscription
                            </TertiaryButton>
                            <SecondaryButton 
                                onClick={handleCancelSubscription}
                                disabled={cancelLoading}
                                className="flex-1 !border-white/40 !text-[#FFBB98]"
                            >
                                {cancelLoading ? 'Canceling...' : 'Yes, Cancel'}
                            </SecondaryButton>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Settings;
